/**
 * SuppliersPage.tsx — Fornitori & Storico (ARCHIVIO). Global, project-
 * independent: a suppliers/contacts directory + a read-only quote-history
 * ledger that feeds the AI price models. Wired to the real Strategia/Fornitori
 * backend (tender-api Step 6) through the strategyApi hooks — no fixtures.
 *
 * Contatti: live CRUD via useSuppliers / useCreateSupplier / useUpdateSupplier /
 * useDeleteSupplier. Storico: read-only ledger via useSupplierHistory. The
 * search box drives both surfaces server-side (passed as `q` to the hooks).
 */
import { useState } from "react";

import { I } from "../../components/icons";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import { apiErrorMessage } from "../../lib/api";
import { fmtEur } from "../../lib/format";
import {
  SUPPLIER_TYPE_LABEL,
  useCreateSupplier,
  useDeleteSupplier,
  useSupplierHistory,
  useSuppliers,
  useUpdateSupplier,
  type Supplier,
  type SupplierInput,
  type SupplierType,
} from "../../lib/strategyApi";

type Tab = "contacts" | "history";

/** ISO date/datetime → `YYYY-MM-DD`, or an em-dash when absent. */
function fmtDate(s: string | null): string {
  if (!s) return "—";
  return s.slice(0, 10);
}

/** Two-letter monogram from the first words of a company name. */
function monogram(name: string): string {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

/** Builds a CSV from the currently-shown suppliers and triggers a download. */
function exportSuppliersCsv(rows: Supplier[]): void {
  const header = [
    "Azienda",
    "Email",
    "Telefono",
    "Categorie",
    "Tipologia",
    "Ultimo uso",
    "N. offerte",
    "Verificato",
  ];
  const esc = (v: string): string => `"${v.replace(/"/g, '""')}"`;
  const lines = [header.map(esc).join(",")];
  for (const s of rows) {
    lines.push(
      [
        s.name,
        s.email ?? "",
        s.phone ?? "",
        s.categories.join(" | "),
        SUPPLIER_TYPE_LABEL[s.supplierType],
        fmtDate(s.lastUsed) === "—" ? "" : fmtDate(s.lastUsed),
        String(s.quoteCount),
        s.verified ? "Sì" : "No",
      ]
        .map(esc)
        .join(","),
    );
  }
  // BOM so Italian accented characters open correctly in Excel.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "fornitori.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Toast ───────────────────────────────────────────────────────────────────
function Toast({ message, onClose }: { message: string; onClose: () => void }): JSX.Element {
  return (
    <div
      className="fade-in"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 16px",
        borderRadius: 10,
        background: "#16191F",
        color: "#F2F1EE",
        boxShadow: "var(--sh-pop)",
        maxWidth: 560,
      }}
    >
      <I.Info size={15} style={{ color: "var(--c-info)", flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          color: "#9CA0AB",
          cursor: "pointer",
          padding: 0,
          marginLeft: 4,
          display: "flex",
        }}
      >
        <I.X size={13} />
      </button>
    </div>
  );
}

// ─── Contacts table ──────────────────────────────────────────────────────────
function ContactsTable({
  suppliers,
  loading,
  error,
  onEdit,
  onDelete,
  deleting,
}: {
  suppliers: Supplier[];
  loading: boolean;
  error: unknown;
  onEdit: (s: Supplier) => void;
  onDelete: (s: Supplier) => void;
  deleting: boolean;
}): JSX.Element {
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th style={{ width: "24%" }}>Azienda</th>
          <th>Email</th>
          <th>Telefono</th>
          <th>Categorie</th>
          <th style={{ width: 150 }}>Tipologia</th>
          <th style={{ width: 110 }}>Ultimo uso</th>
          <th style={{ width: 90, textAlign: "right" }}>N. offerte</th>
          <th style={{ width: 50 }}></th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr>
            <td
              colSpan={8}
              style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
            >
              Caricamento…
            </td>
          </tr>
        )}
        {!loading && error != null && (
          <tr>
            <td
              colSpan={8}
              style={{
                textAlign: "center",
                color: "var(--c-err)",
                padding: "28px 16px",
                fontSize: 12.5,
              }}
            >
              {apiErrorMessage(error)}
            </td>
          </tr>
        )}
        {!loading &&
          error == null &&
          suppliers.map((s) => (
            <tr key={s.id}>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: "var(--c-bg-sunken)",
                      color: "var(--c-ink-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {monogram(s.name)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    {s.verified && (
                      <span
                        className="pill pill-ok"
                        title="Fornitore verificato"
                        style={{ height: 18, fontSize: 10, padding: "0 6px" }}
                      >
                        <I.Check size={9} /> verificato
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td style={{ color: "var(--c-ink-2)", fontSize: 12.5 }}>
                {s.email ? (
                  <>
                    <I.Mail
                      size={11}
                      style={{
                        display: "inline",
                        verticalAlign: "-1px",
                        marginRight: 5,
                        color: "var(--c-ink-3)",
                      }}
                    />
                    {s.email}
                  </>
                ) : (
                  <span style={{ color: "var(--c-ink-4)" }}>—</span>
                )}
              </td>
              <td style={{ color: "var(--c-ink-2)", fontSize: 12.5 }} className="t-mono">
                {s.phone ?? <span style={{ color: "var(--c-ink-4)" }}>—</span>}
              </td>
              <td>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {s.categories.length === 0 ? (
                    <span style={{ color: "var(--c-ink-4)", fontSize: 12 }}>—</span>
                  ) : (
                    s.categories.map((c) => (
                      <span key={c} className="pill">
                        {c}
                      </span>
                    ))
                  )}
                </div>
              </td>
              <td>
                <span className="pill pill-info">{SUPPLIER_TYPE_LABEL[s.supplierType]}</span>
              </td>
              <td style={{ color: "var(--c-ink-3)", fontSize: 12 }}>{fmtDate(s.lastUsed)}</td>
              <td className="t-num t-mono" style={{ textAlign: "right", color: "var(--c-ink-2)" }}>
                {s.quoteCount}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  align="right"
                  width={160}
                  items={[
                    { icon: I.Edit, label: "Modifica", onClick: () => onEdit(s) },
                    { icon: I.Trash, label: "Elimina", danger: true, onClick: () => onDelete(s) },
                  ]}
                  renderTrigger={(toggle) => (
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      onClick={toggle}
                      disabled={deleting}
                    >
                      <I.More size={14} />
                    </button>
                  )}
                />
              </td>
            </tr>
          ))}
        {!loading && error == null && suppliers.length === 0 && (
          <tr>
            <td colSpan={8} style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}>
              Nessun fornitore — aggiungine uno.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ─── History table (read-only ledger) ────────────────────────────────────────
function HistoryTable({ q }: { q: string }): JSX.Element {
  const historyQuery = useSupplierHistory(q || undefined);
  const rows = historyQuery.data ?? [];

  return (
    <>
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid var(--c-line)",
          background: "var(--c-ai-soft)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <I.Sparkle size={14} style={{ color: "var(--c-ai)" }} />
        <span style={{ fontSize: 12, color: "var(--c-ai)", fontWeight: 500 }}>
          Read-only — questo ledger alimenta i modelli predittivi dei prezzi. Non modificabile
          manualmente.
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Progetto</th>
            <th>Fornitore</th>
            <th>Categoria</th>
            <th style={{ textAlign: "right" }}>Importo totale</th>
            <th style={{ textAlign: "right" }}>€/unità</th>
            <th style={{ textAlign: "right" }}>vs Listino</th>
            <th style={{ width: 110 }}>Data</th>
          </tr>
        </thead>
        <tbody>
          {historyQuery.isPending && (
            <tr>
              <td
                colSpan={7}
                style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
              >
                Caricamento…
              </td>
            </tr>
          )}
          {historyQuery.isError && (
            <tr>
              <td
                colSpan={7}
                style={{
                  textAlign: "center",
                  color: "var(--c-err)",
                  padding: "28px 16px",
                  fontSize: 12.5,
                }}
              >
                {apiErrorMessage(historyQuery.error)}
              </td>
            </tr>
          )}
          {!historyQuery.isPending &&
            !historyQuery.isError &&
            rows.map((h) => (
              <tr key={h.quoteId}>
                <td style={{ fontWeight: 500 }}>{h.projectName}</td>
                <td style={{ color: "var(--c-ink-2)" }}>{h.supplierName}</td>
                <td>
                  {h.categoryName ? (
                    <span className="pill">{h.categoryName}</span>
                  ) : (
                    <span style={{ color: "var(--c-ink-4)" }}>—</span>
                  )}
                </td>
                <td
                  className="t-num"
                  style={{ textAlign: "right", fontWeight: 500, fontFamily: "var(--font-mono)" }}
                >
                  {fmtEur(h.total)}
                </td>
                <td
                  className="t-num"
                  style={{
                    textAlign: "right",
                    color: "var(--c-ink-2)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {h.eurUnit != null ? `€ ${h.eurUnit}/${h.unit ?? "u"}` : "—"}
                </td>
                <td
                  className="t-num"
                  style={{
                    textAlign: "right",
                    fontWeight: 500,
                    color:
                      h.vsRegion == null
                        ? "var(--c-ink-4)"
                        : h.vsRegion < 0
                          ? "var(--c-ok)"
                          : "var(--c-err)",
                  }}
                >
                  {h.vsRegion == null
                    ? "—"
                    : `${h.vsRegion >= 0 ? "+" : ""}${h.vsRegion.toFixed(1)}%`}
                </td>
                <td style={{ color: "var(--c-ink-3)", fontSize: 12 }}>{fmtDate(h.decidedAt)}</td>
              </tr>
            ))}
          {!historyQuery.isPending && !historyQuery.isError && rows.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}>
                Nessuna voce di storico.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

// ─── Create / edit modal ─────────────────────────────────────────────────────
function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}): JSX.Element {
  return (
    <label
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "var(--c-ink-2)",
        display: "block",
        marginBottom: 6,
      }}
    >
      {children} {required && <span style={{ color: "var(--c-err)" }}>*</span>}
    </label>
  );
}

function SupplierFormModal({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}): JSX.Element {
  const isEdit = supplier != null;
  const [name, setName] = useState(supplier?.name ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [phone, setPhone] = useState(supplier?.phone ?? "");
  const [categories, setCategories] = useState((supplier?.categories ?? []).join(", "));
  const [supplierType, setSupplierType] = useState<SupplierType>(supplier?.supplierType ?? "both");
  const [verified, setVerified] = useState(supplier?.verified ?? false);
  const [notes, setNotes] = useState(supplier?.notes ?? "");
  const [touched, setTouched] = useState(false);

  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const pending = create.isPending || update.isPending;
  const mutationError = create.error ?? update.error;
  const nameEmpty = name.trim() === "";

  function submit(): void {
    setTouched(true);
    if (nameEmpty) return;
    const input: SupplierInput = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      categories: categories
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean),
      supplierType,
      verified,
      notes: notes.trim() || null,
    };
    if (isEdit && supplier) {
      update.mutate(
        { id: supplier.id, input },
        { onSuccess: () => onSaved(`Fornitore “${input.name}” aggiornato.`) },
      );
    } else {
      create.mutate(input, {
        onSuccess: () => onSaved(`Fornitore “${input.name}” aggiunto.`),
      });
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "var(--c-bg-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        className="fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--c-bg-elev)",
          border: "1px solid var(--c-line)",
          borderRadius: 14,
          boxShadow: "var(--sh-pop)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "var(--c-brand-soft)",
              color: "var(--c-brand)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <I.Suppliers size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {isEdit ? "Modifica fornitore" : "Nuovo fornitore"}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
              Anagrafica globale · indipendente dai progetti
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <FieldLabel required>Azienda</FieldLabel>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Rossi Serramenti S.r.l."
              style={{ width: "100%" }}
            />
            {touched && nameEmpty && (
              <div style={{ fontSize: 11.5, color: "var(--c-err)", marginTop: 5 }}>
                Il nome dell&apos;azienda è obbligatorio.
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Email</FieldLabel>
              <input
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contatti@azienda.it"
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Telefono</FieldLabel>
              <input
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 …"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div>
            <FieldLabel>Categorie</FieldLabel>
            <input
              className="input"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              placeholder="Serramenti, Facciate, Vetro"
              style={{ width: "100%" }}
            />
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 5 }}>
              Separa più categorie con una virgola.
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Tipologia</FieldLabel>
              <select
                className="input"
                value={supplierType}
                onChange={(e) => setSupplierType(e.target.value as SupplierType)}
                style={{ width: "100%" }}
              >
                {(Object.keys(SUPPLIER_TYPE_LABEL) as SupplierType[]).map((t) => (
                  <option key={t} value={t}>
                    {SUPPLIER_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <label
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--c-ink-2)",
                cursor: "pointer",
                height: 32,
              }}
            >
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
              />
              Fornitore verificato
            </label>
          </div>

          <div>
            <FieldLabel>Note</FieldLabel>
            <textarea
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note interne, referenti, condizioni…"
              style={{ width: "100%", height: 76, padding: "8px 10px", resize: "vertical" }}
            />
          </div>

          {mutationError != null && (
            <div
              style={{
                fontSize: 12.5,
                color: "var(--c-err)",
                background: "var(--c-err-soft)",
                border: "1px solid rgba(181, 61, 42, 0.2)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              {apiErrorMessage(mutationError)}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 18px",
            borderTop: "1px solid var(--c-line)",
            background: "var(--c-bg)",
          }}
        >
          <button className="btn btn-sm" onClick={onClose} disabled={pending}>
            Annulla
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={pending}
            style={{ opacity: pending ? 0.6 : 1 }}
          >
            <I.Check size={13} />{" "}
            {pending ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea fornitore"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export function SuppliersPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>("contacts");
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<{ mode: "create" | "edit"; supplier: Supplier | null } | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const q = query.trim();

  const suppliersQuery = useSuppliers(q || undefined);
  const suppliers = suppliersQuery.data ?? [];
  const deleteSupplier = useDeleteSupplier();

  function onDelete(s: Supplier): void {
    if (!window.confirm(`Eliminare il fornitore "${s.name}"?`)) return;
    deleteSupplier.mutate(s.id, {
      onSuccess: () => setToast(`Fornitore “${s.name}” eliminato.`),
      onError: (err) => setToast(`Eliminazione non riuscita: ${apiErrorMessage(err)}`),
    });
  }

  const canExport = suppliers.length > 0;

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>
            Archivio globale
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Fornitori &amp; Storico
          </h1>
          <p style={{ fontSize: 13, color: "var(--c-ink-3)", marginTop: 4 }}>
            Anagrafica indipendente dai progetti. Lo storico delle offerte alimenta i modelli
            predittivi dell&apos;AI.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-sm"
            onClick={() => exportSuppliersCsv(suppliers)}
            disabled={!canExport}
            style={{ opacity: canExport ? 1 : 0.5 }}
            title={
              canExport ? "Esporta i fornitori mostrati in CSV" : "Nessun fornitore da esportare"
            }
          >
            <I.Download size={13} /> Esporta CSV
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setModal({ mode: "create", supplier: null })}
          >
            <I.Plus size={13} /> Nuovo fornitore
          </button>
        </div>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")}>
            Contatti fornitori
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")}>
            Storico offerte
            <span className="ai-badge" style={{ marginLeft: 8, height: 14, fontSize: 9 }}>
              <I.Sparkle size={8} />
              AI training
            </span>
          </TabBtn>
          <div style={{ flex: 1 }} />
          <div style={{ padding: "0 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <I.Search
                size={13}
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--c-ink-4)",
                }}
              />
              <input
                className="input"
                placeholder="Cerca…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ height: 28, fontSize: 12, paddingLeft: 26, width: 200 }}
              />
            </div>
          </div>
        </div>

        {tab === "contacts" ? (
          <ContactsTable
            suppliers={suppliers}
            loading={suppliersQuery.isPending}
            error={suppliersQuery.isError ? suppliersQuery.error : null}
            onEdit={(s) => setModal({ mode: "edit", supplier: s })}
            onDelete={onDelete}
            deleting={deleteSupplier.isPending}
          />
        ) : (
          <HistoryTable q={q} />
        )}
      </div>

      {modal && (
        <SupplierFormModal
          supplier={modal.supplier}
          onClose={() => setModal(null)}
          onSaved={(msg) => {
            setModal(null);
            setToast(msg);
          }}
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
